import React, { useRef, useState } from 'react';
import { Plus, Trash, PencilSimple, Eye, X, UploadSimple, Shuffle, Check, ArrowsClockwise, ArrowUDownLeft, UserPlus, Printer, DownloadSimple, ChatTeardropText } from '@phosphor-icons/react';
import { classEntryAPI } from '../../utils/api';
import api from '../../utils/api';
import { toast } from 'sonner';
import { RiderAutocomplete } from './RiderAutocomplete';
import { isSameDay } from '../../utils/sameDay';

export const EntryTable = ({
  selectedClass, classEntries, showState, drawGenerated,
  onAddEntry, showAddEntry, onSetShowAddEntry,
  riderName, onSetRiderName, horseName, onSetHorseName,
  editingEntry, onStartEditing, onCancelEdit, onSaveEntry,
  onDeleteEntry, onTogglePayment, onCSVImport, onGenerateDraw,
  onReverseDraw, onCopyDraw,
  onClosePanel, onGoToScoring, onViewLive, csvInputRef,
  onSubmitTime, allClasses, allSidepots, onRolloverEntry, onClearAllEntries,
  show, showId, onQuickAddEntries, onReloadEntries,
  // v240 — When false, the entries list still renders (with rider · horse ·
  // draw # · time) so contestants and viewers can see who's up when, but
  // producer-only controls (Add / Import / Draw / Edit / Delete / Payment
  // toggle / Rollover / Time entry / Print) are hidden.
  isAdmin = true,
}) => {
  const [timeInputs, setTimeInputs] = useState({});
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [rolloverEntryId, setRolloverEntryId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [reverseDrawMenu, setReverseDrawMenu] = useState(false);
  const [copyDrawSourceId, setCopyDrawSourceId] = useState('');
  const [copyDrawReverse, setCopyDrawReverse] = useState(true);
  // v248 — Class-level draw_reversed flips the *running order* (high→low)
  // while keeping every rider's draw number intact. dragSize (v248) is
  // the announcer's "drag size" — every N entries the printed draw sheet
  // inserts a small visual gap.
  const drawReversed = !!selectedClass?.draw_reversed;
  const dragSize = Math.max(0, parseInt(selectedClass?.drag_size, 10) || 0);
  const drawSort = (a, b) => {
    const an = a.draw_number, bn = b.draw_number;
    if (an && bn) return drawReversed ? bn - an : an - bn;
    if (an) return -1;
    if (bn) return 1;
    return 0;
  };
  // Producer-side multi-class selector on the Add Entry form. Defaults to
  // empty (the currently-selected class is implicitly always added). Keyed
  // by class id.
  const [extraClassIds, setExtraClassIds] = useState(new Set());
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddEntries, setQuickAddEntries] = useState({});
  const [quickAddSelected, setQuickAddSelected] = useState(new Set());
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddSourceClass, setQuickAddSourceClass] = useState('');
  const [editingDrawId, setEditingDrawId] = useState(null);
  // Which entry's special-request popover is open. null when closed.
  const [showRequestForId, setShowRequestForId] = useState(null);
  const [drawInput, setDrawInput] = useState('');
  // Bulk-select state for "Received Check" workflow: producers tick a
  // batch of UNPAID/PENDING entries then mark them all as PAID in one
  // call. Keeps the mail-in-only flow (v211) usable at scale.
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const timeRefs = useRef({});
  const drawRef = useRef(null);

  const startEditDraw = (entry) => {
    setEditingDrawId(entry.id);
    setDrawInput(entry.draw_number ? String(entry.draw_number) : '');
    setTimeout(() => drawRef.current?.focus(), 50);
  };

  const saveDraw = async (entry) => {
    const raw = drawInput.trim();
    const newNum = raw === '' ? null : parseInt(raw, 10);
    if (raw !== '' && (!Number.isFinite(newNum) || newNum <= 0)) {
      setEditingDrawId(null);
      return;
    }
    try {
      await classEntryAPI.setDrawNumber(showId, selectedClass.id, entry.id, newNum);
      onReloadEntries && onReloadEntries();
    } catch { /* noop */ }
    setEditingDrawId(null);
  };

  const handleClearDraw = async () => {
    if (!window.confirm('Clear all draw numbers for this class?')) return;
    try {
      await classEntryAPI.clearDraw(showId, selectedClass.id);
      onReloadEntries && onReloadEntries();
    } catch { /* noop */ }
  };

  // === Draw: Print & Export Excel ===
  const exportDrawExcel = async () => {
    try {
      const resp = await api.get(`/shows/${showId}/classes/${selectedClass.id}/draw/xlsx`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const safeClass = (selectedClass.name || 'class').replace(/[^a-z0-9]+/gi, '_');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safeClass}_draw.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.error('Failed to export draw');
    }
  };

  const printDraw = () => {
    const title = `${show?.name || ''} — ${selectedClass.name} · Draw Order${drawReversed ? ' (Reverse Order)' : ''}`;
    const drawEntries = [...classEntries].sort(drawSort);
    // Build id -> name lookup for the show's sidepots so we can render
    // human-readable sidepot names in the draw sheet instead of ids.
    const sidepotNameById = {};
    (allSidepots || []).forEach((sp) => { sidepotNameById[sp.id] = sp.name; });
    const escape = (s) => String(s || '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const rows = drawEntries.map((e, i) => {
      const spNames = (e.sidepot_ids || [])
        .map((id) => sidepotNameById[id])
        .filter(Boolean)
        .join(', ');
      // v248 — Drag-spacing gap. Every `dragSize` entries insert a small
      // spacer row so the announcer can see where each drag ends. Only
      // when the producer has configured a positive drag size on the class.
      const isDragBreak = dragSize > 0 && i > 0 && i % dragSize === 0;
      const spacer = isDragBreak
        ? `<tr class="drag-gap"><td colspan="5"></td></tr>`
        : '';
      return `${spacer}<tr${i % 2 === 0 ? ' style="background:#F5F7FA"' : ''}>
        <td class="c" style="font-weight:700">${e.draw_number ?? '—'}</td>
        <td>${escape(e.rider_name)}</td>
        <td>${escape(e.horse_name)}</td>
        <td class="sp">${escape(spNames) || '<span style="color:#bbb">—</span>'}</td>
        <td class="c">&nbsp;</td>
      </tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>${escape(title)}</title><meta charset="utf-8"/>
      <style>
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:24px;margin:0}
        h1{margin:0 0 4px;font-size:20px}
        .sub{color:#666;font-size:13px;margin-bottom:2px;font-weight:600}
        .total{color:#888;font-size:11px;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:12px;table-layout:fixed}
        thead{display:table-header-group}
        tr{page-break-inside:avoid}
        th{text-align:left;padding:7px 9px;background:#0A0A0A;color:#fff;font-weight:700;border:1px solid #0A0A0A}
        td{padding:7px 9px;border-bottom:1px solid #ddd;border-right:1px solid #eee;vertical-align:middle;word-wrap:break-word}
        td:first-child{border-left:1px solid #eee}
        td.c{text-align:center}
        td.sp{font-size:11px;color:#444}
        tr.drag-gap td{border:0 !important;background:transparent !important;height:16px;padding:0}
        tr.drag-gap{page-break-inside:avoid}
        @media print{@page{margin:0.4in}}
      </style></head><body>
      <h1>${escape(show?.name || '')}</h1>
      <div class="sub">${escape(selectedClass.name)} · Draw Order${drawReversed ? ' <span style=\"color:#AF52DE;font-weight:800\">(REVERSE ORDER)</span>' : ''}</div>
      <div class="total">Total Entries: ${drawEntries.length}${dragSize ? ` · Drag every ${dragSize}` : ''} · Printed ${new Date().toLocaleString()}</div>
      <table>
        <colgroup>
          <col style="width:10%"/>
          <col style="width:28%"/>
          <col style="width:24%"/>
          <col style="width:26%"/>
          <col style="width:12%"/>
        </colgroup>
        <thead><tr>
          <th style="text-align:center">Draw #</th>
          <th>Rider</th>
          <th>Horse</th>
          <th>Sidepots</th>
          <th style="text-align:center">Time</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=()=>{window.print()};</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const submitTime = (entry) => {
    const val = timeInputs[entry.id]?.trim();
    if (!val) return;
    // Identify the next unscored entry in draw order BEFORE submitting,
    // so we can auto-focus its input after the score round-trips. Huge
    // QOL for office staff entering a stream of times: type → Enter →
    // jump → type → Enter, no mouse.
    const orderedNow = [...classEntries].sort(drawSort);
    const curIdx = orderedNow.findIndex(e => e.id === entry.id);
    const nextEntry = orderedNow.slice(curIdx + 1).find(e => {
      const st = e.score?.status;
      // "Unscored" = no score record, or status is empty/PENDING.
      return !st || st === '';
    });

    const scrMarkers = ['scr', 'scratch'];
    const ntMarkers = ['nt', 'no time', 'dns', 'dnf', 'to'];
    if (scrMarkers.includes(val.toLowerCase())) {
      onSubmitTime(entry, null, 'SCR');
    } else if (ntMarkers.includes(val.toLowerCase())) {
      onSubmitTime(entry, null, 'NT');
    } else {
      const time = parseFloat(val);
      if (!isNaN(time) && time > 0) {
        onSubmitTime(entry, time, 'COMPLETED');
      }
    }
    setTimeInputs(prev => ({ ...prev, [entry.id]: '' }));
    setEditingTimeId(null);

    // Auto-advance: focus the next unscored entry's input after re-render.
    // 250ms tracks the LiveResults page behaviour so reload+focus stays
    // in lockstep with the score insertion round-trip.
    if (nextEntry) {
      setTimeout(() => {
        const nextInput = timeRefs.current[nextEntry.id]
          || document.querySelector(`[data-testid="time-input-${nextEntry.id}"]`);
        if (nextInput) {
          nextInput.focus();
          try { nextInput.select(); } catch { /* no-op */ }
        }
      }, 250);
    }
  };

  const handleTimeKeyDown = (e, entry) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitTime(entry);
    } else if (e.key === 'Escape') {
      setEditingTimeId(null);
      setTimeInputs(prev => ({ ...prev, [entry.id]: '' }));
    }
  };

  const startEditingTime = (entry) => {
    setEditingTimeId(entry.id);
    const currentTime = ['SCRATCH', 'SCR', 'NT'].includes(entry.score?.status) ? (entry.score.status === 'SCR' || entry.score.status === 'SCRATCH' ? 'SCR' : 'NT') : entry.score?.final_time?.toFixed(3) || '';
    setTimeInputs(prev => ({ ...prev, [entry.id]: currentTime }));
    setTimeout(() => timeRefs.current[entry.id]?.focus(), 50);
  };

  const sorted = [...classEntries].sort(drawSort);

  const isMultiDay = show && show.start_date !== show.end_date;
  const otherClasses = (allClasses || []).filter(c => c.id !== selectedClass.id);

  const openQuickAdd = async () => {
    setShowQuickAdd(true);
    setQuickAddLoading(true);
    setQuickAddSelected(new Set());
    setQuickAddSourceClass('');
    try {
      const { data } = await classEntryAPI.listAll(showId || show?.id);
      // Group entries by class_id
      const byClass = {};
      for (const e of (data.entries || data || [])) {
        const cid = e.class_id;
        if (!byClass[cid]) byClass[cid] = [];
        byClass[cid].push(e);
      }
      setQuickAddEntries(byClass);
    } catch { setQuickAddEntries({}); }
    finally { setQuickAddLoading(false); }
  };

  const getSourceEntries = () => {
    if (!quickAddSourceClass || !quickAddEntries[quickAddSourceClass]) return [];
    const existingKeys = new Set(classEntries.map(e => `${e.rider_name?.toLowerCase()}|${e.horse_name?.toLowerCase()}`));
    return quickAddEntries[quickAddSourceClass].filter(e => !existingKeys.has(`${e.rider_name?.toLowerCase()}|${e.horse_name?.toLowerCase()}`));
  };

  const toggleQuickSelect = (entryId) => {
    setQuickAddSelected(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId); else next.add(entryId);
      return next;
    });
  };

  const selectAllQuick = () => {
    const entries = getSourceEntries();
    if (quickAddSelected.size === entries.length) setQuickAddSelected(new Set());
    else setQuickAddSelected(new Set(entries.map(e => e.id)));
  };

  const submitQuickAdd = () => {
    const entries = getSourceEntries().filter(e => quickAddSelected.has(e.id));
    if (entries.length === 0) return;
    onQuickAddEntries(entries);
    setShowQuickAdd(false);
  };

  return (
    <>
      {/* Header */}
      <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-[#262626] flex items-center justify-between">
        <div className="flex items-center gap-2 pr-2 min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{selectedClass.name}</h3>
          {drawReversed && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#AF52DE]/15 border border-[#AF52DE]/40 text-[#AF52DE] flex-shrink-0"
              data-testid="draw-reversed-badge"
              title="Running order is high→low. Draw numbers unchanged."
            >
              <ArrowUDownLeft size={11} weight="bold" /> Reverse Order
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button onClick={onViewLive} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-[#9E9E9E]/10 text-[#9E9E9E] border border-[#9E9E9E]/20 rounded-md text-xs sm:text-sm hover:bg-[#9E9E9E]/20 transition-colors" data-testid="view-live-results-btn">
            <Eye size={12} /> <span className="hidden sm:inline">Live</span>
          </button>
          <button onClick={onClosePanel} className="p-1.5 hover:bg-[#262626] rounded transition-colors" data-testid="close-entries-panel">
            <X size={16} className="text-[#9E9E9E]" />
          </button>
        </div>
      </div>

      {/* Actions Bar — producer/admin only. v240: hidden from viewers so
          contestants see just the entry list + draw numbers. */}
      {isAdmin && (
      <div className="px-3 sm:px-5 py-2 sm:py-3 border-b border-[#262626] flex gap-1.5 sm:gap-2 flex-wrap">
        <button onClick={() => { onSetShowAddEntry(true); onCancelEdit(); onSetRiderName(''); onSetHorseName(''); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF] text-white rounded-md text-sm font-semibold hover:bg-[#0096FF]/80 transition-colors" data-testid="add-entry-btn">
          <Plus size={14} weight="bold" /> Add Entry
        </button>
        <button onClick={() => csvInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#9E9E9E]/10 text-[#9E9E9E] border border-[#9E9E9E]/20 rounded-md text-sm font-semibold hover:bg-[#9E9E9E]/20 transition-colors" data-testid="import-entries-class-btn">
          <UploadSimple size={14} weight="bold" /> Import
        </button>
        {otherClasses.length > 0 && onQuickAddEntries && (
          <button onClick={openQuickAdd} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/20 rounded-md text-sm font-semibold hover:bg-[#FF9500]/20 transition-colors" data-testid="quick-add-btn">
            <UserPlus size={14} weight="bold" /> Quick Add
          </button>
        )}
        <input ref={csvInputRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.docx,.doc" onChange={onCSVImport} className="hidden" data-testid="class-file-input" />
        <button onClick={onGenerateDraw} disabled={classEntries.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 rounded-md text-sm font-semibold hover:bg-[#34C759]/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="generate-draw-btn">
          <Shuffle size={14} weight="bold" /> {drawGenerated ? 'Re-Draw' : 'Generate Draw'}
        </button>
        {drawGenerated && (
          <button onClick={handleClearDraw} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/20 rounded-md text-sm font-semibold hover:bg-[#FF9500]/20 transition-colors" data-testid="clear-draw-btn" title="Clear all draw numbers so you can manually assign them">
            Clear Draw
          </button>
        )}
        {(classEntries.length > 0 && (onReverseDraw || onCopyDraw)) && (
          <div className="relative">
            <button
              onClick={() => setReverseDrawMenu(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#AF52DE]/10 text-[#AF52DE] border border-[#AF52DE]/20 rounded-md text-sm font-semibold hover:bg-[#AF52DE]/20 transition-colors"
              data-testid="reverse-draw-btn"
              title="Reverse or copy draw from another class"
            >
              <ArrowUDownLeft size={14} weight="bold" /> Reverse Draw
            </button>
            {reverseDrawMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setReverseDrawMenu(false)} />
                <div
                  className="absolute top-full left-0 mt-1 w-80 bg-[#111] border border-[#262626] rounded-lg shadow-2xl z-50 p-3"
                  data-testid="reverse-draw-menu"
                >
                  {drawGenerated && onReverseDraw && (
                    <button
                      onClick={() => { setReverseDrawMenu(false); onReverseDraw(); }}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-[#AF52DE]/10 transition-colors mb-2"
                      data-testid="reverse-current-draw-btn"
                    >
                      <p className="text-sm text-white font-semibold flex items-center gap-2">
                        <ArrowUDownLeft size={13} weight="bold" className="text-[#AF52DE]" />
                        Flip current draw
                      </p>
                      <p className="text-[11px] text-[#9E9E9E] mt-0.5 pl-5">
                        Swap 1↔N, 2↔N-1, etc. Same class.
                      </p>
                    </button>
                  )}
                  {onCopyDraw && (allClasses || []).filter(c => c.id !== selectedClass?.id).length > 0 && (
                    <div className="border-t border-[#262626] pt-2">
                      <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-semibold px-3 pb-2">
                        Copy from another class
                      </p>
                      <select
                        value={copyDrawSourceId}
                        onChange={(e) => setCopyDrawSourceId(e.target.value)}
                        className="w-full mb-2 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-xs focus:outline-none focus:border-[#AF52DE]"
                        data-testid="copy-draw-source-select"
                      >
                        <option value="">Select source class...</option>
                        {(allClasses || [])
                          .filter(c => c.id !== selectedClass?.id)
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.day ? ` (${c.day})` : ''}
                            </option>
                          ))}
                      </select>
                      <label className="flex items-center gap-2 px-1 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={copyDrawReverse}
                          onChange={(e) => setCopyDrawReverse(e.target.checked)}
                          className="w-3.5 h-3.5 accent-[#AF52DE]"
                          data-testid="copy-draw-reverse-check"
                        />
                        <span className="text-xs text-[#E5E5E5]">Reverse order (50→1)</span>
                      </label>
                      <button
                        onClick={() => {
                          if (!copyDrawSourceId) { toast.error('Pick a source class first'); return; }
                          setReverseDrawMenu(false);
                          onCopyDraw(copyDrawSourceId, copyDrawReverse);
                        }}
                        disabled={!copyDrawSourceId}
                        className="w-full px-3 py-2 bg-[#AF52DE] hover:bg-[#AF52DE]/80 text-white text-xs font-bold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="copy-draw-apply-btn"
                      >
                        Apply
                      </button>
                      <p className="text-[10px] text-[#6B6B6B] mt-2 px-1 leading-snug">
                        Riders are matched by name + horse. Any rider not in the source class is
                        left without a draw number.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {classEntries.length > 0 && (
          <>
            <button
              onClick={printDraw}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-sm font-semibold hover:text-white hover:border-[#0096FF]/50 transition-colors"
              data-testid="print-draw-btn"
              title="Print draw order (or Save as PDF)"
            >
              <Printer size={14} weight="bold" /> Print Draw
            </button>
            <button
              onClick={exportDrawExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-sm font-semibold hover:text-white hover:border-[#34C759]/50 transition-colors"
              data-testid="export-draw-btn"
              title="Download draw order as Excel"
            >
              <DownloadSimple size={14} weight="bold" /> Export Draw
            </button>
          </>
        )}
        {classEntries.length > 0 && !confirmClear && (
          <>
            {(() => {
              // "Select all unpaid" — quick way to grab every UNPAID/
              // PENDING row for the mail-in-check workflow. Hidden when
              // there's nothing unpaid to select.
              const unpaidIds = classEntries
                .filter((e) => e.payment_status !== 'PAID' && e.payment_status !== 'COMP')
                .map((e) => e.id);
              if (unpaidIds.length === 0) return null;
              const allSelected = unpaidIds.every((id) => bulkSelected.has(id));
              return (
                <button
                  onClick={() => {
                    if (allSelected) setBulkSelected(new Set());
                    else setBulkSelected(new Set(unpaidIds));
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-sm font-semibold hover:text-white hover:border-[#34C759]/50 transition-colors"
                  data-testid="select-all-unpaid-btn"
                  title="Select all unpaid entries for bulk mark-paid"
                >
                  <Check size={14} weight="bold" />
                  {allSelected ? 'Deselect' : 'Select'} All Unpaid ({unpaidIds.length})
                </button>
              );
            })()}
            <button onClick={() => setConfirmClear(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 rounded-md text-sm font-semibold hover:bg-[#FF3B30]/20 transition-colors ml-auto" data-testid="clear-all-entries-btn">
              <Trash size={14} weight="bold" /> Clear All
            </button>
          </>
        )}
        {confirmClear && (
          <div className="flex items-center gap-2 ml-auto bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-md px-3 py-1.5" data-testid="clear-all-confirm">
            <span className="text-[#FF3B30] text-xs font-semibold">Delete all {classEntries.length} entries?</span>
            <button onClick={() => { onClearAllEntries(); setConfirmClear(false); }} className="px-2.5 py-1 bg-[#FF3B30] text-white text-xs font-bold rounded transition-colors hover:bg-[#FF3B30]/80" data-testid="clear-all-confirm-yes">Yes, Delete</button>
            <button onClick={() => setConfirmClear(false)} className="px-2.5 py-1 text-[#9E9E9E] text-xs font-semibold hover:text-white transition-colors" data-testid="clear-all-confirm-no">Cancel</button>
          </div>
        )}
      </div>
      )}

      {/* Add/Edit Entry Form */}
      {isAdmin && (showAddEntry || editingEntry) && (
        <div className="px-5 py-4 border-b border-[#262626] bg-[#0A0A0A]/50" data-testid="entry-form">
          <h4 className="text-sm font-semibold text-[#0096FF] mb-3">{editingEntry ? 'Edit Entry' : 'New Entry'}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Rider Name *</label>
              <RiderAutocomplete
                value={riderName}
                onChange={onSetRiderName}
                onHorseChange={(h) => onSetHorseName(h)}
                showId={showId || show?.id}
                placeholder="Rider name"
                testId="entry-rider-name"
              />
            </div>
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Horse Name</label>
              <input value={horseName} onChange={(e) => onSetHorseName(e.target.value)} placeholder="Horse name"
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                data-testid="entry-horse-name" />
            </div>
          </div>
          {/* Multi-class picker — only shown when adding (not editing). */}
          {!editingEntry && (allClasses || []).filter(c => c.id !== selectedClass?.id).length > 0 && (
            <div className="mb-3" data-testid="multi-class-picker">
              <label className="block text-xs text-[#9E9E9E] mb-2">
                Also add this rider to these classes <span className="text-[#555]">(optional)</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[180px] overflow-y-auto bg-[#0A0A0A] border border-[#262626] rounded-md p-2">
                {(allClasses || [])
                  .filter(c => c.id !== selectedClass?.id)
                  .map((c) => {
                    const checked = extraClassIds.has(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-xs ${checked ? 'bg-[#0096FF]/10 border border-[#0096FF]/40' : 'bg-[#161616] border border-transparent hover:border-[#262626]'}`}
                        data-testid={`multi-class-option-${c.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(extraClassIds);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            setExtraClassIds(next);
                          }}
                          className="w-3.5 h-3.5 accent-[#0096FF]"
                        />
                        <span className="text-white truncate">{c.name}</span>
                        {c.scheduled_day && <span className="text-[#6B6B6B] text-[10px] flex-shrink-0">{c.scheduled_day}</span>}
                      </label>
                    );
                  })}
              </div>
              {extraClassIds.size > 0 && (
                <p className="text-[11px] text-[#0096FF] mt-1" data-testid="multi-class-count">
                  Will add to {1 + extraClassIds.size} classes total ({selectedClass?.name} + {extraClassIds.size} more)
                </p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (editingEntry) { onSaveEntry(); return; }
                const ids = Array.from(extraClassIds);
                onAddEntry(ids);
                setExtraClassIds(new Set());
              }}
              className="px-4 py-2 bg-[#0096FF] text-white text-sm font-semibold rounded-md hover:bg-[#0096FF]/80"
              data-testid="save-entry-btn"
            >
              {editingEntry ? 'Save' : (extraClassIds.size > 0 ? `Add to ${1 + extraClassIds.size} Classes` : 'Add')}
            </button>
            <button onClick={() => { onCancelEdit(); onSetShowAddEntry(false); setExtraClassIds(new Set()); }}
              className="px-4 py-2 bg-[#262626] text-[#9E9E9E] text-sm font-semibold rounded-md hover:bg-[#333]"
              data-testid="cancel-entry-btn">Cancel</button>
          </div>
        </div>
      )}

      {/* Quick Add Panel */}
      {showQuickAdd && (
        <div className="px-3 sm:px-5 py-4 border-b border-[#262626] bg-[#0A0A0A]/50" data-testid="quick-add-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[#FF9500]">Quick Add from Another Class</h4>
            <button onClick={() => setShowQuickAdd(false)} className="text-[#9E9E9E] hover:text-white"><X size={16} /></button>
          </div>

          {/* Source class picker */}
          <select
            value={quickAddSourceClass}
            onChange={(e) => { setQuickAddSourceClass(e.target.value); setQuickAddSelected(new Set()); }}
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500] mb-3"
            data-testid="quick-add-source-select"
          >
            <option value="">Select a class to copy from...</option>
            {otherClasses.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.scheduled_day ? ` (${c.scheduled_day})` : ''}
              </option>
            ))}
          </select>

          {quickAddLoading && <p className="text-[#9E9E9E] text-xs">Loading entries...</p>}

          {quickAddSourceClass && !quickAddLoading && (
            <>
              {getSourceEntries().length === 0 ? (
                <p className="text-[#9E9E9E] text-xs py-2">No new entries to add — all riders are already in this class.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <button onClick={selectAllQuick} className="text-[#FF9500] text-xs font-semibold hover:text-[#FF9500]/80" data-testid="quick-add-select-all">
                      {quickAddSelected.size === getSourceEntries().length ? 'Deselect All' : `Select All (${getSourceEntries().length})`}
                    </button>
                    <span className="text-[#9E9E9E] text-xs">{quickAddSelected.size} selected</span>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1 mb-3">
                    {getSourceEntries().map(e => (
                      <div key={e.id} onClick={() => toggleQuickSelect(e.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-colors ${
                          quickAddSelected.has(e.id) ? 'bg-[#FF9500]/10 border border-[#FF9500]/30' : 'bg-[#0A0A0A] border border-transparent hover:border-[#262626]'
                        }`} data-testid={`quick-add-entry-${e.id}`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          quickAddSelected.has(e.id) ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[#555]'
                        }`}>
                          {quickAddSelected.has(e.id) && <span className="text-black text-[10px] font-bold">&#10003;</span>}
                        </div>
                        <span className="text-white text-sm">{e.rider_name}</span>
                        {e.horse_name && <span className="text-[#9E9E9E] text-xs">— {e.horse_name}</span>}
                      </div>
                    ))}
                  </div>
                  <button onClick={submitQuickAdd} disabled={quickAddSelected.size === 0}
                    className="w-full py-2 bg-[#FF9500] text-white text-sm font-bold rounded-md hover:bg-[#FF9500]/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="quick-add-submit">
                    Add {quickAddSelected.size} Entr{quickAddSelected.size === 1 ? 'y' : 'ies'} to {selectedClass.name}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Bulk "check received" mark-paid bar — shown only when at
          least one entry row is checked. Batch endpoint mutates all
          selected entries at once so producers processing mail-in
          checks aren't clicking payment one at a time. */}
      {isAdmin && bulkSelected.size > 0 && (
        <div className="mx-3 sm:mx-5 mb-2 px-3 py-2 bg-[#34C759]/10 border border-[#34C759]/30 rounded-md flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-actions-bar">
          <span className="text-[#34C759] text-xs font-semibold">
            {bulkSelected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkSelected(new Set())}
              className="px-3 py-1.5 text-[10px] text-[#9E9E9E] hover:text-white font-semibold transition-colors"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
            <button
              type="button"
              disabled={bulkLoading}
              onClick={async () => {
                if (bulkLoading) return;
                const ids = Array.from(bulkSelected);
                if (!window.confirm(`Mark ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'} as PAID (check received)?`)) return;
                setBulkLoading(true);
                try {
                  // Backend caps at 500 per call — safe for our UI cap
                  // of "everything visible" being far less than that.
                  const { data } = await api.post(`/shows/${showId}/entries/bulk-mark-paid`, { entry_ids: ids });
                  toast.success(`Marked ${data.updated} entr${data.updated === 1 ? 'y' : 'ies'} as PAID`);
                  setBulkSelected(new Set());
                  if (onReloadEntries) onReloadEntries();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to mark paid');
                } finally {
                  setBulkLoading(false);
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#34C759] hover:bg-[#34C759]/90 text-black text-xs font-bold rounded-md transition-colors disabled:opacity-50"
              data-testid="bulk-mark-paid-btn"
            >
              <Check size={12} weight="bold" /> Mark PAID
            </button>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="flex-1 overflow-y-auto" style={{maxHeight: 'calc(100vh - 400px)'}}>
        {classEntries.length === 0 ? (
          <div className="py-12 text-center" data-testid="entries-empty-state">
            <p className="text-[#9E9E9E] text-sm">No entries yet</p>
            <p className="text-[#555] text-xs mt-1">{isAdmin ? 'Click "Add Entry" or "Import" to get started' : 'Draws will appear here once the producer posts them'}</p>
          </div>
        ) : (
          <div className="divide-y divide-[#262626]/50">
            {sorted.map((entry) => {
              const hasScore = entry.score && entry.score.status;
              const isScored = hasScore && entry.score.status === 'COMPLETED';
              const isScratch = hasScore && (entry.score.status === 'SCRATCH' || entry.score.status === 'SCR' || entry.score.status === 'NT');
              return (
                <div key={entry.id} className="px-3 sm:px-5 py-2.5 sm:py-3 hover:bg-[#161616] group transition-colors" data-testid={`entry-row-${entry.id}`}>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Bulk-select checkbox — shown only for UNPAID/PENDING
                        entries so producers can batch mark checks received
                        (mail-in workflow) without accidentally toggling a
                        PAID entry back into limbo. */}
                    {isAdmin && entry.payment_status !== 'PAID' && entry.payment_status !== 'COMP' && (
                      <input
                        type="checkbox"
                        checked={bulkSelected.has(entry.id)}
                        onChange={(e) => {
                          setBulkSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(entry.id);
                            else next.delete(entry.id);
                            return next;
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-[#34C759] cursor-pointer flex-shrink-0"
                        data-testid={`bulk-select-${entry.id}`}
                        title="Select for bulk mark-paid"
                      />
                    )}
                    {/* Draw number — click to edit (admin) or read-only pill (viewer). */}
                    {isAdmin && editingDrawId === entry.id ? (
                      <input
                        ref={drawRef}
                        type="number" min="1"
                        value={drawInput}
                        onChange={(e) => setDrawInput(e.target.value)}
                        onBlur={() => saveDraw(entry)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveDraw(entry); }
                          if (e.key === 'Escape') { setEditingDrawId(null); }
                        }}
                        placeholder="#"
                        className="w-12 h-8 text-center bg-[#0A0A0A] border border-[#0096FF] rounded-full text-[#0096FF] text-sm font-black focus:outline-none"
                        data-testid={`draw-number-input-${entry.id}`}
                      />
                    ) : entry.draw_number ? (
                      isAdmin ? (
                        <button
                          type="button"
                          onClick={() => startEditDraw(entry)}
                          className="w-8 h-8 flex items-center justify-center bg-[#0096FF]/10 text-[#0096FF] border border-[#0096FF]/30 rounded-full text-sm font-black flex-shrink-0 hover:bg-[#0096FF]/25 transition-colors cursor-pointer"
                          title="Click to edit draw number"
                          data-testid={`draw-number-${entry.id}`}
                        >
                          {entry.draw_number}
                        </button>
                      ) : (
                        <span
                          className="w-8 h-8 flex items-center justify-center bg-[#0096FF]/10 text-[#0096FF] border border-[#0096FF]/30 rounded-full text-sm font-black flex-shrink-0"
                          data-testid={`draw-number-${entry.id}`}
                        >
                          {entry.draw_number}
                        </span>
                      )
                    ) : isAdmin ? (
                      <button
                        type="button"
                        onClick={() => startEditDraw(entry)}
                        className="w-8 h-8 flex items-center justify-center bg-[#262626] text-[#555] rounded-full text-sm flex-shrink-0 hover:bg-[#0096FF]/10 hover:text-[#0096FF] transition-colors cursor-pointer"
                        title="Click to assign a draw number"
                        data-testid={`draw-number-empty-${entry.id}`}
                      >
                        +
                      </button>
                    ) : (
                      <span
                        className="w-8 h-8 flex items-center justify-center bg-[#262626] text-[#555] rounded-full text-sm flex-shrink-0"
                        data-testid={`draw-number-empty-${entry.id}`}
                      >
                        &ndash;
                      </span>
                    )}

                    {/* Rider Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-semibold truncate">{entry.rider_name}</p>
                        {isAdmin ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onTogglePayment(entry); }}
                          data-testid={`payment-badge-${entry.id}`}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase cursor-pointer transition-colors ${
                            entry.payment_status === 'PAID' ? 'bg-[#34C759]/15 text-[#34C759] hover:bg-[#34C759]/25' :
                            entry.payment_status === 'COMP' ? 'bg-[#0096FF]/15 text-[#0096FF] hover:bg-[#0096FF]/25' :
                            entry.payment_status === 'PENDING' ? 'bg-[#FACC15]/15 text-[#FACC15] hover:bg-[#FACC15]/25' :
                            'bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30]/25'
                          }`}
                          title="Click to toggle payment status"
                        >
                          {entry.payment_status || 'UNPAID'}
                        </button>
                        ) : null}
                        {/* Rollover-source ("from") and rollover-target ("to")
                            badges live on the horse-name row below — same
                            compact treatment so the rider name stays full
                            width. See the row beneath. */}
                        {/* Rider-submitted special request — only shown when present.
                            Click the bubble to read the note in a popover. */}
                        {entry.special_request && entry.special_request.trim() && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); setShowRequestForId(showRequestForId === entry.id ? null : entry.id); }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#AF52DE]/15 text-[#AF52DE] rounded text-[10px] font-bold hover:bg-[#AF52DE]/25 transition-colors"
                              title="Rider has a special request — click to read"
                              data-testid={`special-request-badge-${entry.id}`}
                            >
                              <ChatTeardropText size={11} weight="fill" /> NOTE
                            </button>
                            {showRequestForId === entry.id && (
                              <div
                                className="absolute z-20 left-0 top-full mt-1 w-72 max-w-[80vw] bg-[#161616] border border-[#AF52DE]/40 rounded-md shadow-xl p-3"
                                onClick={(ev) => ev.stopPropagation()}
                                data-testid={`special-request-popover-${entry.id}`}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#AF52DE]">Special Request</span>
                                  <button onClick={() => setShowRequestForId(null)} className="text-[#9E9E9E] hover:text-white" data-testid={`special-request-close-${entry.id}`}>
                                    <X size={12} />
                                  </button>
                                </div>
                                <p className="text-white text-xs leading-relaxed whitespace-pre-wrap">{entry.special_request}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {(entry.horse_name || (Array.isArray(entry.rolled_to) && entry.rolled_to.length > 0) || entry.is_rollover) && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          {entry.horse_name && <p className="text-[#9E9E9E] text-xs truncate min-w-0">{entry.horse_name}</p>}
                          {/* Rollover-SOURCE badge — when THIS entry arrived via
                              a rollover. Same compact "R/O from {Source}" style
                              as the rolled-to badge so the rider+horse name
                              stays readable. Orange because it points back to
                              the source class. */}
                          {entry.is_rollover && (() => {
                            const src = entry.rollover_source_class_id
                              ? (allClasses || show?.classes || []).find(c => c.id === entry.rollover_source_class_id)
                              : null;
                            const label = src ? src.name : 'previous class';
                            return (
                              <span
                                className="inline-flex items-center px-1 py-0 bg-[#FF9500]/15 text-[#FF9500] rounded text-[9px] font-bold normal-case leading-none flex-shrink-0"
                                data-testid={`rollover-entry-badge-${entry.id}`}
                                title={`Rolled from: ${label}`}
                              >
                                R/O from {label}
                              </span>
                            );
                          })()}
                          {Array.isArray(entry.rolled_to) && entry.rolled_to.length > 0 && (() => {
                            const labels = entry.rolled_to.map(r => r.class_name || r.sidepot_name || 'Unknown').join(', ');
                            const isSidepot = entry.rolled_to.some(r => r.sidepot_id);
                            return (
                              <span
                                className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-bold normal-case leading-none flex-shrink-0 ${isSidepot ? 'bg-[#FACC15]/15 text-[#FACC15]' : 'bg-[#FF9500]/15 text-[#FF9500]'}`}
                                data-testid={`rolled-to-badge-${entry.id}`}
                                title={`Rolled to: ${labels}`}
                              >
                                R/O {labels}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Time display or input — v240: viewers see read-only
                        times; admin gets the full inline editor. */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isAdmin ? (
                        entry.score?.final_time != null && entry.score?.status === 'COMPLETED' ? (
                          <span
                            className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/30 rounded text-[#34C759] text-xs font-mono font-bold"
                            data-testid={`time-display-${entry.id}`}
                          >
                            {entry.score.final_time.toFixed(3)}
                          </span>
                        ) : entry.score?.status === 'SCRATCH' || entry.score?.status === 'NT' ? (
                          <span
                            className="px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-[#FF3B30] text-xs font-bold"
                            data-testid={`time-display-${entry.id}`}
                          >
                            NT
                          </span>
                        ) : (
                          <span className="text-[#555] text-xs font-mono px-2">&mdash;</span>
                        )
                      ) : editingTimeId === entry.id ? (
                        /* Editing existing time */
                        <>
                          <input
                            ref={el => { timeRefs.current[entry.id] = el; }}
                            type="text"
                            placeholder="Time or NT"
                            value={timeInputs[entry.id] || ''}
                            onChange={(e) => setTimeInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                            onKeyDown={(e) => handleTimeKeyDown(e, entry)}
                            className="w-20 px-2 py-1 bg-[#0A0A0A] border border-[#0096FF] rounded text-white text-xs font-mono text-center focus:outline-none"
                            data-testid={`time-input-${entry.id}`}
                            autoFocus
                          />
                          <button
                            onClick={() => submitTime(entry)}
                            className="p-1 bg-[#34C759] hover:bg-[#2DB84D] rounded transition-colors"
                            title="Save time"
                            data-testid={`time-save-${entry.id}`}
                          >
                            <Check size={12} weight="bold" className="text-white" />
                          </button>
                          <button
                            onClick={() => { setEditingTimeId(null); setTimeInputs(prev => ({ ...prev, [entry.id]: '' })); }}
                            className="p-1 bg-[#262626] hover:bg-[#333] rounded transition-colors"
                            title="Cancel"
                            data-testid={`time-cancel-${entry.id}`}
                          >
                            <X size={12} className="text-[#9E9E9E]" />
                          </button>
                        </>
                      ) : isScored ? (
                        /* Display scored time — click to edit */
                        <button
                          onClick={() => startEditingTime(entry)}
                          className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/30 rounded text-[#34C759] text-xs font-mono font-bold hover:bg-[#34C759]/20 transition-colors cursor-pointer"
                          title="Click to edit time"
                          data-testid={`time-display-${entry.id}`}
                        >
                          {entry.score.final_time?.toFixed(3)}
                        </button>
                      ) : isScratch ? (
                        /* Display NT — click to edit */
                        <button
                          onClick={() => startEditingTime(entry)}
                          className="px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-[#FF3B30] text-xs font-bold hover:bg-[#FF3B30]/20 transition-colors cursor-pointer"
                          title="Click to edit"
                          data-testid={`time-display-${entry.id}`}
                        >
                          NT
                        </button>
                      ) : (
                        /* No score yet — show input with save button */
                        <>
                          <input
                            ref={el => { timeRefs.current[entry.id] = el; }}
                            type="text"
                            placeholder="Time"
                            value={timeInputs[entry.id] || ''}
                            onChange={(e) => setTimeInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                            onKeyDown={(e) => handleTimeKeyDown(e, entry)}
                            className="w-20 px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs font-mono text-center focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
                            data-testid={`time-input-${entry.id}`}
                          />
                          {timeInputs[entry.id]?.trim() && (
                            <button
                              onClick={() => submitTime(entry)}
                              className="p-1 bg-[#34C759] hover:bg-[#2DB84D] rounded transition-colors"
                              title="Save time"
                              data-testid={`time-save-${entry.id}`}
                            >
                              <Check size={12} weight="bold" className="text-white" />
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Actions — producer/admin only (v240). */}
                    {isAdmin && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      {((allClasses && allClasses.length > 1) || (allSidepots && allSidepots.length > 0)) && onRolloverEntry && (
                        <button onClick={(e) => { e.stopPropagation(); setRolloverEntryId(rolloverEntryId === entry.id ? null : entry.id); }} className={`p-1.5 rounded transition-colors ${rolloverEntryId === entry.id ? 'bg-[#FF9500]/20' : 'hover:bg-[#FF9500]/10'}`} title="Rollover to another class or sidepot" data-testid={`rollover-btn-${entry.id}`}>
                          <ArrowsClockwise size={14} className="text-[#FF9500]" />
                        </button>
                      )}
                      <button onClick={() => onStartEditing(entry)} className="p-1.5 hover:bg-[#262626] rounded transition-colors" title="Edit" data-testid={`edit-entry-${entry.id}`}>
                        <PencilSimple size={14} className="text-[#9E9E9E]" />
                      </button>
                      <button onClick={() => onDeleteEntry(entry.id, entry)} className="p-1.5 hover:bg-[#FF3B30]/10 rounded transition-colors" title="Remove" data-testid={`delete-entry-${entry.id}`}>
                        <Trash size={14} className="text-[#FF3B30]" />
                      </button>
                    </div>
                    )}
                  </div>
                  {/* Rollover class picker — producer/admin only */}
                  {isAdmin && rolloverEntryId === entry.id && (() => {
                    // v264 — Producer rollover picker must respect the
                    // same-day rule (matches rider self-entry map + the
                    // backend POST enforcement). Fallback: allow when
                    // either side lacks a scheduled day (legacy shows).
                    const rolloverClasses = (allClasses || []).filter(c => c.id !== selectedClass.id && isSameDay(selectedClass, c));
                    const rolloverSidepots = (allSidepots || []).filter(sp => isSameDay(selectedClass, sp));
                    return (
                    <div className="mt-2 ml-11 p-3 bg-[#0A0A0A] border border-[#FF9500]/30 rounded-lg" data-testid={`rollover-picker-${entry.id}`}>
                      <p className="text-[#FF9500] text-xs font-bold mb-2">Roll over to:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {rolloverClasses.length === 0 && rolloverSidepots.length === 0 && (
                          <span className="text-[11px] text-[#9E9E9E] italic">No same-day rollover targets available</span>
                        )}
                        {rolloverClasses.map(cls => (
                          <button
                            key={cls.id}
                            onClick={() => { onRolloverEntry(entry, cls.id); setRolloverEntryId(null); }}
                            className="px-3 py-1.5 bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] text-xs font-semibold rounded-md hover:bg-[#FF9500]/20 transition-colors"
                            data-testid={`rollover-to-${cls.id}`}
                          >
                            {cls.name}
                          </button>
                        ))}
                        {rolloverSidepots.map(sp => {
                          const already = (entry.sidepot_ids || []).includes(sp.id);
                          return (
                            <button
                              key={sp.id}
                              disabled={already}
                              onClick={() => { onRolloverEntry(entry, null, sp.id); setRolloverEntryId(null); }}
                              className={`px-3 py-1.5 border text-xs font-semibold rounded-md transition-colors ${already ? 'bg-[#262626] border-[#262626] text-[#555] cursor-not-allowed' : 'bg-[#FF9500]/5 border-[#FF9500]/30 text-[#FF9500] hover:bg-[#FF9500]/20'}`}
                              data-testid={`rollover-to-sidepot-${sp.id}`}
                              title={already ? 'Already in this sidepot' : `Add ${sp.name} sidepot`}
                            >
                              {sp.name}
                              <span className="ml-1 text-[9px] font-bold uppercase tracking-wide">Sidepot</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {classEntries.length > 0 && (
        <div className="px-5 py-3 border-t border-[#262626] bg-[#0A0A0A]/50 flex items-center justify-between text-xs text-[#9E9E9E]">
          <span>{classEntries.length} entries</span>
          <span>{classEntries.filter(e => e.score?.status === 'COMPLETED').length} timed</span>
          <span>Pool: ${(classEntries.length * (selectedClass.entry_fee || 0) * (selectedClass.payback_percent || 70) / 100).toFixed(0)}</span>
        </div>
      )}
    </>
  );
};
